import { Bell } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button, Dialog } from '@/components/ui';
import { useReminders } from '@/features/shell/api';
import styles from './Shell.module.css';

/**
 * RL-1820: a support issue opens Go-live on its Support tab (the API links
 * the page only).
 */
function reminderHref(group: string, href: string) {
  return group === 'support' && href === '/admin/go-live'
    ? '/admin/go-live?tab=support_ticket'
    : href;
}

/** "Bell · reminders": everything waiting for this role in one list (#1). */
export function ReminderBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data } = useReminders();
  const count = (data ?? []).reduce((n, g) => n + g.items.length, 0);

  return (
    <>
      <button
        type="button"
        className={styles.iconButton}
        aria-label={
          count
            ? `${t('shell.notifications')} (${count})`
            : t('shell.notifications')
        }
        onClick={() => setOpen(true)}
      >
        <Bell size={17} />
        {count > 0 && (
          <span className={styles.bellBadge} aria-hidden="true">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      <Dialog
        open={open}
        title="Reminders"
        onClose={() => setOpen(false)}
        footer={
          <Button onClick={() => setOpen(false)}>{t('common.close')}</Button>
        }
      >
        {count === 0 ? (
          <p className={styles.searchNote}>Nothing is waiting for you.</p>
        ) : (
          <div className={styles.reminders}>
            {data!.map((g) => (
              <section key={g.key} aria-label={g.label}>
                <p className={styles.reminderHead}>
                  <span className="eyebrow">{g.label}</span>
                  <span className={styles.countPill}>{g.items.length}</span>
                </p>
                <ul>
                  {g.items.map((i) => (
                    <li key={i.id} className={styles.reminderItem}>
                      <span className={styles.reminderDot} aria-hidden="true" />
                      <span className={styles.reminderText}>
                        <strong>{i.title}</strong>
                        <span>{i.detail}</span>
                      </span>
                      <Button
                        size="sm"
                        onClick={() => {
                          setOpen(false);
                          navigate(reminderHref(g.key, i.href));
                        }}
                      >
                        Open
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </Dialog>
    </>
  );
}
